// The specific reasons this module's backend gives, in the four languages.
//
// Two codes travel INSIDE an error and are not the code it is thrown with:
//
//   · `reasons` — what a generic refusal carries in `details.reason` or
//     `details.code` (`ConflictError(…, 'CONFLICT', { reason: 'ALREADY_CLOSED' })`).
//     errorMessage() says it from `errors.reasons.<REASON>` instead of the
//     generic «that record is no longer in the state you expected».
//   · `fields` — the code of each field of a ValidationError, said by
//     fieldErrors() from `errors.fields.<CODE>` instead of «not a valid value».
//
// Generic field codes (EMPTY, IN_THE_FUTURE, NOT_FOUND…) are the shell's and
// are not repeated here. platform/web/tests/errorReasons.test.js reads the
// backend and fails on a code without its four sentences, and on two
// modules saying one code differently (the shell merges them into one).

const errorReasons = {
  es: {
    reasons: {
      CONTRACT_SUPPLIER_FIXED: 'Un contrato es del proveedor con el que se firmó',
      REQUISITION_CHANGED: 'Alguien acaba de cambiar esta solicitud: vuelve a abrirla',
      REQUISITION_CLOSED: 'Esta solicitud ya está cerrada y no se puede retirar',
      REQUISITION_DECIDED: 'Esta solicitud ya está decidida y no se puede cambiar',
      REQUISITION_NOT_APPROVED: 'Solo se compra una solicitud aprobada',
      REQUISITION_NOT_PENDING: 'Esta solicitud ya no está pendiente de decidir',
    },
    fields: {
      USE_RECEIVE_ENDPOINT: 'La mercancía se da por recibida con la recepción, no cambiando el estado',
    },
  },
  ca: {
    reasons: {
      CONTRACT_SUPPLIER_FIXED: 'Un contracte és del proveïdor amb qui es va signar',
      REQUISITION_CHANGED: 'Algú acaba de canviar aquesta sol·licitud: torna-la a obrir',
      REQUISITION_CLOSED: 'Aquesta sol·licitud ja està tancada i no es pot retirar',
      REQUISITION_DECIDED: 'Aquesta sol·licitud ja està decidida i no es pot canviar',
      REQUISITION_NOT_APPROVED: 'Només es compra una sol·licitud aprovada',
      REQUISITION_NOT_PENDING: 'Aquesta sol·licitud ja no està pendent de decidir',
    },
    fields: {
      USE_RECEIVE_ENDPOINT: 'La mercaderia es dona per rebuda amb la recepció, no canviant l’estat',
    },
  },
  en: {
    reasons: {
      CONTRACT_SUPPLIER_FIXED: 'A contract belongs to the supplier it was signed with',
      REQUISITION_CHANGED: 'Somebody has just changed this requisition: open it again',
      REQUISITION_CLOSED: 'This requisition is already closed and cannot be withdrawn',
      REQUISITION_DECIDED: 'This requisition has been decided and cannot be changed',
      REQUISITION_NOT_APPROVED: 'Only an approved requisition can be bought',
      REQUISITION_NOT_PENDING: 'This requisition is no longer waiting for a decision',
    },
    fields: {
      USE_RECEIVE_ENDPOINT: 'Goods are received through the goods receipt, not by changing the status',
    },
  },
  fr: {
    reasons: {
      CONTRACT_SUPPLIER_FIXED: 'Un contrat appartient au fournisseur avec lequel il a été signé',
      REQUISITION_CHANGED: 'Quelqu’un vient de modifier cette demande : rouvrez-la',
      REQUISITION_CLOSED: 'Cette demande est déjà close et ne peut pas être retirée',
      REQUISITION_DECIDED: 'Cette demande a été tranchée et ne peut plus être modifiée',
      REQUISITION_NOT_APPROVED: 'Seule une demande approuvée peut être achetée',
      REQUISITION_NOT_PENDING: 'Cette demande n’attend plus de décision',
    },
    fields: {
      USE_RECEIVE_ENDPOINT: 'Les marchandises se réceptionnent par la réception, pas en changeant le statut',
    },
  },
}

/** The module's catalogue with these hung from `errors`, locale by locale. */
export function withErrorReasons(base) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    const own = errorReasons[locale] ?? {}
    merged[locale] = {
      ...catalogue,
      errors: {
        ...catalogue.errors,
        reasons: { ...catalogue.errors?.reasons, ...own.reasons },
        fields: { ...catalogue.errors?.fields, ...own.fields },
      },
    }
  }
  return merged
}

export default errorReasons
